import { BasicButton, InputTextArea, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useCaseMutation from "@/hooks/useCaseMutation";
import { QUERY_KEYS } from "@/libraries/constant";
import { getCasePdfFile } from "@/queries";
import { ExportCasePayloadSchema } from "@/schemas";
import { usePrompt } from "@/stores/prompt";
import { zodResolver } from "@hookform/resolvers/zod";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { onlineManager, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import omit from "lodash/omit";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/case/view/$id/export")({
	loader: ({ context, params }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureQueryData(getCasePdfFile(params.id));
		}
		return context.queryClient.getQueryData([
			QUERY_KEYS.GET_CASE_PDF_FILE,
			params.id,
		]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation("dashboard");
	const caseId = Route.useParams({
		select: (state) => state.id,
	});
	const {
		control,
		handleSubmit,
		formState: { isValid, isDirty, isSubmitting },
	} = useForm<ExportCasePayloadSchema>({
		mode: "all",
		resolver: zodResolver(ExportCasePayloadSchema),
		defaultValues: {
			caseId: caseId,
			emails: "",
			note: "",
		},
	});

	const onBack = useBackward();
	const openPrompt = usePrompt((state) => state.open);
	const { sendEmail } = useCaseMutation();
	const casePdf = useSuspenseQuery(getCasePdfFile(caseId));

	const onSubmit: SubmitHandler<ExportCasePayloadSchema> = (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		const onSendEmail = async () => {
			try {
				await sendEmail.mutateAsync(data);
				await onBack();
				return toast.success(t("dashboard:case.exportEmailSuccess"));
			} catch (error) {
				return toast.error(t("dashboard:case.exportEmailFailed"));
			}
		};
		return openPrompt({
			title: t("dashboard:case.consentPromptTitle"),
			positiveButton: {
				label: t("dashboard:case.yes"),
				variant: "contained",
				color: "primary",
				onClick: onSendEmail,
			},
			negativeButton: {
				label: t("dashboard:case.cancel"),
				variant: "outlined",
				color: "primary",
			},
		});
	};

	return (
		<>
			<BackAppBar title={t("dashboard:case.exportCaseInfo")} className="pb-3">
				<Alert
					icon={<InfoRoundedIcon className="!fill-primary" />}
					severity="info"
					className="!bg-blue-100"
				>
					<Typography fontSize={12}>
						{t("dashboard:case.exportCaseInfoWarning")}
					</Typography>
				</Alert>
			</BackAppBar>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="container bg-white flex flex-col flex-1"
			>
				<div className="flex flex-col flex-1 py-4 gap-6">
					<Controller
						control={control}
						name="emails"
						render={({ field, fieldState }) => (
							<TextField
								id={field.name}
								type="email"
								inputMode="email"
								label={t("dashboard:case.patientEmail")}
								helperText={fieldState.error?.message}
								error={!!fieldState.error?.message}
								className="[&_.MuiFormLabel-asterisk]:text-red-500 [&_.MuiInputBase-root]:!rounded-xl"
								{...omit(field, ["ref"])}
							/>
						)}
					/>
					<Controller
						control={control}
						name="note"
						render={({ field, fieldState }) => (
							<InputTextArea
								fullWidth
								rows={4}
								label={t("dashboard:case.noteToPatient")}
								maxLength={200}
								error={!!fieldState.error?.message}
								{...omit(field, ["ref"])}
							/>
						)}
					/>
					<div className="w-fit space-y-2">
						<Typography fontSize={14}>
							{t("dashboard:case.attachment")}
						</Typography>
						<div className="w-fit border border-gray-100 rounded-xl">
							<IconButton
								LinkComponent="a"
								target="_blank"
								className="!rounded-xl"
								href={URL.createObjectURL(casePdf.data)}
								title={t("dashboard:case.summaryOfMedicalReport", { caseId })}
							>
								<InsertDriveFileRoundedIcon />
							</IconButton>
						</div>
					</div>
				</div>
				<div className="container py-4 shadow">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						className="w-full !h-12 text-sm font-semibold !shadow-none"
					>
						{t("dashboard:case.submit")}
					</BasicButton>
				</div>
			</form>
		</>
	);
}
